/**
 * 微信支付服务
 * 处理微信支付相关逻辑
 */

import { logDebug, logError, logInfo, showError } from '../utils/errorHandler';
import { request } from './api';

/**
 * 微信支付参数接口
 */
export interface WechatPayParams {
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: string;
  paySign: string;
}

/**
 * JS-SDK 配置参数接口
 */
interface JSAPIConfig {
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
}

// 全局状态管理
let wxReady = false;
let wxConfiging = false;

/**
 * 检测微信 JS-SDK 是否已加载
 */
function checkWechatSDK(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).wx !== 'undefined';
}

/**
 * 等待微信 JS-SDK 加载
 */
function waitForWechatSDK(timeout: number = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (checkWechatSDK()) {
      logDebug('[WechatPay] SDK already loaded');
      resolve();
      return;
    }

    logDebug('[WechatPay] Waiting for SDK to load', { timeout });
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (checkWechatSDK()) {
        clearInterval(checkInterval);
        const elapsed = Date.now() - startTime;
        logInfo('[WechatPay] SDK loaded', { elapsed: `${elapsed}ms` });
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        const error = new Error(`微信 JS-SDK 加载超时 (${timeout}ms)`);
        logError('[WechatPay] SDK load timeout', { timeout, elapsed: Date.now() - startTime });
        reject(error);
      }
    }, 100);
  });
}

/**
 * 初始化微信 JS-SDK
 * 向后端获取签名参数，调用 wx.config() 配置
 */
export async function initWxJSSDK(projectId?: string): Promise<void> {
  if (wxConfiging) {
    logInfo('[WechatPay] wx.config() already in progress, waiting...');
    // 等待配置完成
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (!wxConfiging) {
          clearInterval(checkInterval);
          if (wxReady) {
            resolve();
          } else {
            reject(new Error('微信 JS-SDK 配置失败'));
          }
        }
      }, 100);
      
      // 超时保护
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('等待 wx.config() 超时'));
      }, 10000);
    });
  }

  if (wxReady) {
    logInfo('[WechatPay] wx.config() already completed');
    return;
  }

  wxConfiging = true;
  logInfo('[WechatPay] Starting wx.config() initialization');

  try {
    // 等待 SDK 加载
    await waitForWechatSDK();

    const wx = (window as any).wx;
    if (!wx) {
      throw new Error('微信 JS-SDK 未加载');
    }

    // 获取当前页面 URL（移除 # 及其后面的部分）
    // 强制使用 HTTPS，确保微信 JS-SDK 签名使用正确的协议
    let urlForSign = window.location.href.split('#')[0];
    if (urlForSign.startsWith('http://')) {
      urlForSign = urlForSign.replace('http://', 'https://');
      logInfo('[WechatPay] Converted HTTP URL to HTTPS', { original: window.location.href, converted: urlForSign });
    }
    logInfo('[WechatPay] Getting JS-SDK signature - 微信支付', { url: urlForSign, projectId });

    // 向后端获取签名参数
    // 注意：request 函数会自动添加 API_BASE_URL（已包含 /api），所以这里不需要 /api 前缀
    const url = projectId 
      ? `/wx/jssdk-sign?url=${encodeURIComponent(urlForSign)}&projectId=${encodeURIComponent(projectId)}`
      : `/wx/jssdk-sign?url=${encodeURIComponent(urlForSign)}`;
    
    const response = await request<JSAPIConfig>(url, {
      method: 'GET',
    });

    logInfo('[WechatPay] JS-SDK signature received - 微信支付', {
      appId: response.appId,
      timestamp: response.timestamp,
      hasNonceStr: !!response.nonceStr,
      hasSignature: !!response.signature,
      urlForSign,
    });

    // 调用 wx.config()
    wx.config({
      debug: false,
      appId: response.appId,
      timestamp: response.timestamp,
      nonceStr: response.nonceStr,
      signature: response.signature,
      jsApiList: ['chooseWXPay'],
    });

    // 等待 wx.ready() 回调
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wxReady = true;
        wxConfiging = false;
        logInfo('[WechatPay] wx.ready() callback triggered, wx.config() completed');
        resolve();
      });

      wx.error((err: any) => {
        wxReady = false;
        wxConfiging = false;
        logError('[WechatPay] wx.error() callback triggered', { err });
        const errorMsg = err?.errMsg || JSON.stringify(err);
        showError(`微信 JS-SDK 配置失败: ${errorMsg}\n请刷新页面重试`, 'WechatPay.initWxJSSDK');
        reject(new Error(`微信 JS-SDK 配置失败: ${errorMsg}`));
      });
    });
  } catch (error: any) {
    wxReady = false;
    wxConfiging = false;
    logError('[WechatPay] initWxJSSDK error', {
      error,
      errorMessage: error?.message,
    });
    showError(`微信 JS-SDK 初始化失败: ${error?.message || '未知错误'}\n请刷新页面重试`, 'WechatPay.initWxJSSDK');
    throw error;
  }
}

/**
 * 调起微信支付
 * @param paymentParams 支付参数
 * @param projectId 项目ID（可选，用于获取公募机构配置）
 */
export async function chooseWXPay(paymentParams: WechatPayParams, projectId?: string): Promise<void> {
  // 记录当前页面的完整 URL（用于调试支付授权目录问题）
  const currentPageUrl = window.location.href;
  const currentPageUrlWithoutHash = window.location.href.split('#')[0];
  const currentHost = window.location.host;
  const currentPathname = window.location.pathname;
  const currentSearch = window.location.search;
  
  logInfo('[WechatPay] chooseWXPay called - 微信支付', { 
    timeStamp: paymentParams.timeStamp,
    nonceStr: paymentParams.nonceStr,
    package: paymentParams.package,
    signType: paymentParams.signType,
    hasPaySign: !!paymentParams.paySign,
    allParams: Object.keys(paymentParams),
    // 添加当前页面 URL 信息（用于调试支付授权目录问题）
    currentPageUrl,
    currentPageUrlWithoutHash,
    currentHost,
    currentPathname,
    currentSearch,
    projectId
  });
  
  // 重要日志：记录调用 wx.chooseWXPay() 时的页面 URL（确保上报）
  logInfo('[WechatPay] IMPORTANT: About to call wx.chooseWXPay() - 微信支付', {
    currentPageUrl,
    currentPageUrlWithoutHash,
    currentHost,
    currentPathname,
    currentSearch
  });
  
  // 同时输出到控制台（方便调试）
  console.log('[WechatPay] IMPORTANT: About to call wx.chooseWXPay()');
  console.log('[WechatPay] IMPORTANT: Current page URL (full):', currentPageUrl);
  console.log('[WechatPay] IMPORTANT: Current page URL (without hash):', currentPageUrlWithoutHash);
  console.log('[WechatPay] IMPORTANT: Current host:', currentHost);
  console.log('[WechatPay] IMPORTANT: Current pathname:', currentPathname);
  console.log('[WechatPay] IMPORTANT: This URL will be verified by WeChat Pay authorization directory');
  
  // 验证支付参数完整性
  const requiredFields: (keyof WechatPayParams)[] = ['timeStamp', 'nonceStr', 'package', 'signType', 'paySign'];
  const missingFields = requiredFields.filter(field => !paymentParams[field]);
  if (missingFields.length > 0) {
    const error = new Error(`支付参数不完整，缺少字段: ${missingFields.join(', ')}\n当前参数: ${JSON.stringify(paymentParams, null, 2)}`);
    logError('[WechatPay] Payment params incomplete - 微信支付', { missingFields, paymentParams });
    throw error;
  }

  try {
    // 确保 wx.config() 已完成
    if (!wxReady) {
      logInfo('[WechatPay] wx.config() not ready, initializing...');
      // 如果没初始化过就先初始化
      await initWxJSSDK(projectId);
    }

    // 再次检查（防止初始化失败）
    if (!wxReady) {
      const error = new Error('微信环境校验中，请稍后再试（或刷新页面）');
      logError('[WechatPay] wx.config() not ready after initialization');
      showError(error.message, 'WechatPay.chooseWXPay');
      throw error;
    }

    // 等待微信 JS-SDK 加载（双重保险）
    await waitForWechatSDK();

    const wx = (window as any).wx;
    logInfo('[WechatPay] Checking wx object', {
      hasWx: !!wx,
      hasChooseWXPay: !!(wx && wx.chooseWXPay),
      wxReady,
      wxKeys: wx ? Object.keys(wx).slice(0, 10) : []
    });
    
    if (!wx || !wx.chooseWXPay) {
      const error = new Error(`微信 JS-SDK 未正确加载\nwx 对象: ${wx ? '存在' : '不存在'}\nchooseWXPay 方法: ${wx && wx.chooseWXPay ? '存在' : '不存在'}\n请确保微信 JS-SDK 已正确加载`);
      logError('[WechatPay] SDK method not found', { 
        wx: !!wx, 
        chooseWXPay: !!(wx && wx.chooseWXPay),
        wxKeys: wx ? Object.keys(wx) : []
      });
      throw error;
    }

    return new Promise((resolve, reject) => {
      // 再次记录当前页面 URL（确保是最新的）
      const finalPageUrl = window.location.href.split('#')[0];
      logInfo('[WechatPay] About to call wx.chooseWXPay - 微信支付', {
        timestamp: paymentParams.timeStamp,
        nonceStr: paymentParams.nonceStr,
        package: paymentParams.package,
        signType: paymentParams.signType,
        hasPaySign: !!paymentParams.paySign,
        finalPageUrl,
        currentHost: window.location.host,
        currentPathname: window.location.pathname
      });
      
      // 重要日志：记录最终页面 URL（确保上报）
      logInfo('[WechatPay] IMPORTANT: Final page URL before wx.chooseWXPay() - 微信支付', {
        finalPageUrl,
        currentHost: window.location.host,
        currentPathname: window.location.pathname,
        message: 'WeChat will verify this URL against payment authorization directory'
      });
      
      // 同时输出到控制台（方便调试）
      console.log('[WechatPay] IMPORTANT: Final page URL before wx.chooseWXPay():', finalPageUrl);
      console.log('[WechatPay] IMPORTANT: Current host:', window.location.host);
      console.log('[WechatPay] IMPORTANT: Current pathname:', window.location.pathname);
      console.log('[WechatPay] IMPORTANT: Current search:', window.location.search);
      console.log('[WechatPay] IMPORTANT: WeChat will verify this URL against payment authorization directory');
      console.log('[WechatPay] IMPORTANT: If payment fails immediately, check if this URL is registered in WeChat Pay merchant platform');
      
      wx.chooseWXPay({
        timestamp: paymentParams.timeStamp,
        nonceStr: paymentParams.nonceStr,
        package: paymentParams.package,
        signType: paymentParams.signType,
        paySign: paymentParams.paySign,
        success: () => {
          logInfo('[WechatPay] wx.chooseWXPay success callback triggered - 微信支付');
          resolve();
        },
        cancel: () => {
          // 记录取消时的页面信息，因为某些错误（如 "not registered"）可能会触发 cancel 而不是 fail
          const cancelPageUrl = window.location.href.split('#')[0];
          const cancelDetails = {
            currentPageUrl: window.location.href,
            currentPageUrlWithoutHash: cancelPageUrl,
            currentHost: window.location.host,
            currentPathname: window.location.pathname,
            currentSearch: window.location.search,
            note: 'Payment was cancelled. If this happens immediately after calling wx.chooseWXPay, it might be due to payment authorization directory configuration issue (e.g., "not registered" error).'
          };
          logInfo('[WechatPay] wx.chooseWXPay cancel callback triggered - 微信支付', cancelDetails);
          // 同时输出到控制台
          console.log('[WechatPay] IMPORTANT: Payment cancelled');
          console.log('[WechatPay] IMPORTANT: Cancel page URL:', cancelPageUrl);
          console.log('[WechatPay] IMPORTANT: If payment was cancelled immediately, check payment authorization directory configuration');
          reject(new Error('用户取消支付'));
        },
        fail: (err: any) => {
          const errorPageUrl = window.location.href.split('#')[0];
          // logError 总是会上报，所以这里保持原样
          // 提取详细的错误信息
          const errorMessage = err?.errMsg || err?.message || '支付失败';
          const errString = JSON.stringify(err, null, 2);
          // 检查是否包含 "not registered" 相关错误
          const hasNotRegisteredError = errString.toLowerCase().includes('not registered') || 
                                        errString.toLowerCase().includes('未注册') ||
                                        errorMessage.toLowerCase().includes('not registered') ||
                                        errorMessage.toLowerCase().includes('未注册');
          const errorDetails = {
            errMsg: err?.errMsg,
            message: err?.message,
            error: errorMessage,
            errType: typeof err,
            errKeys: err ? Object.keys(err) : [],
            errString: errString, // 完整的错误对象字符串
            errorPageUrl,
            currentPageUrl: window.location.href,
            currentPageUrlWithoutHash: errorPageUrl,
            currentHost: window.location.host,
            currentPathname: window.location.pathname,
            currentSearch: window.location.search,
            // 添加重要信息用于调试支付授权目录问题
            paymentAuthDirectoryIssue: true,
            hasNotRegisteredError: hasNotRegisteredError, // 标记是否为 "not registered" 错误
            note: 'This error may be related to payment authorization directory configuration. Check if the domain is registered in WeChat Pay merchant platform.'
          };
          logError('[WechatPay] wx.chooseWXPay fail callback triggered - 微信支付', errorDetails);
          
          // 同时输出到控制台（方便调试）
          console.error('[WechatPay] IMPORTANT: wx.chooseWXPay() failed');
          console.error('[WechatPay] IMPORTANT: Error page URL:', errorPageUrl);
          console.error('[WechatPay] IMPORTANT: Error details:', err);
          console.error('[WechatPay] IMPORTANT: Error string:', errString);
          if (hasNotRegisteredError) {
            console.error('[WechatPay] IMPORTANT: This is a "not registered" error - check payment authorization directory configuration');
          }
          
          const errorMsg = err?.errMsg || err?.message || '支付失败';
          reject(new Error(`支付失败: ${errorMsg}\n错误详情: ${errString}\n当前页面URL: ${errorPageUrl}`));
        },
      });
      
      logInfo('[WechatPay] wx.chooseWXPay called, waiting for callback - 微信支付');
    });
  } catch (error: any) {
    logError('[WechatPay] chooseWXPay error caught - 微信支付', {
      error,
      errorType: error?.constructor?.name,
      errorMessage: error?.message,
      errorStack: error?.stack?.split('\n').slice(0, 5).join('\n')
    });
    throw error;
  }
}
